/**
 * Single source of truth for SEO.
 * Imported by the React app (Seo component, public pages) AND by
 * scripts/generate-seo.mjs (sitemap.xml + robots.txt). Keep it dependency-free.
 *
 * Rules applied here:
 *  - <title> <= 60-65 characters
 *  - meta description ~150 characters (145-155)
 *  - keywords: <= 20 words in total, comma separated
 */

export const SITE_URL = 'https://primeface.io';
export const SITE_NAME = 'PrimeFace';
export const DEFAULT_LANG = 'fr';
export const LANGS = ['fr', 'en'];
export const LOCALES = { fr: 'fr_FR', en: 'en_US' };
export const OG_IMAGE_PATH = '/og-image.png';
export const LOGO_PATH = '/icon-512.png';
export const SUPPORT_EMAIL = 'support@primeface.io';
export const PUBLISHER = 'OpenMeta SAS';
export const LEGAL_EMAIL = 'support@openmeta.tech';
export const LEGAL_ADDRESS = '15 chemin Veilloti\u00e8re, 61330 Ceauc\u00e9, France';
export const LEGAL_POSTAL = { streetAddress: '15 chemin Veilloti\u00e8re', postalCode: '61330', addressLocality: 'Ceauc\u00e9', addressCountry: 'FR' };
export const LEGAL_DIRECTOR = { fr: 'Le repr\u00e9sentant l\u00e9gal d\u2019OpenMeta SAS', en: 'The legal representative of OpenMeta SAS' };
export const LEGAL_HOSTS = 'Gandi SAS (63-65 boulevard Mass\u00e9na, 75013 Paris, France) et OVH SAS (2 rue Kellermann, 59100 Roubaix, France)';
export const LEGAL_HOSTS_EN = 'Gandi SAS (63-65 boulevard Mass\u00e9na, 75013 Paris, France) and OVH SAS (2 rue Kellermann, 59100 Roubaix, France)';
/** Google Search Console: HTML-file verification served from public/ (see also GOOGLE_SITE_VERIFICATION meta slot). */
export const GOOGLE_SITE_VERIFICATION_FILE = 'google52cd770a9e9ce420.html';
/** Google Search Console: paste the `content` value of the google-site-verification meta tag,
 *  then run `node scripts/generate-seo.mjs` to inject it into public/index.html. */
export const GOOGLE_SITE_VERIFICATION = '';
/** Last revision of the legal documents (ISO date). */
export const LEGAL_UPDATED = '2026-10-19';

/** Brand tagline reused in JSON-LD and fallbacks. */
export const TAGLINE = {
  fr: 'Coach IA en optimisation esth\u00e9tique et relooking strat\u00e9gique (Looksmaxxing).',
  en: 'AI coach for aesthetic optimization and strategic makeover (Looksmaxxing).',
};

/**
 * Target keyword clusters (FR/EN), inferred from the looksmaxxing / men's makeover niche.
 * Used to build per-page meta keywords, the WebSite JSON-LD and the FAQ long-tail questions.
 * To swap in a client-provided list: edit these arrays, then review PUBLIC_PAGES.*.meta.*.keywords
 * (<= 20 words each) and run `node scripts/generate-seo.mjs`. See src/seo/SEO_KEYWORDS.md.
 */
export const TARGET_KEYWORDS = {
  fr: {
    primary: ['looksmaxxing', 'looksmaxxing homme', 'relooking homme', 'coach IA looksmaxxing', 'glow up homme', 'analyse visage IA', 'simulation avant apr\u00e8s'],
    secondary: ['am\u00e9liorer son apparence', 'grooming homme', 'style vestimentaire homme', 'soin de la peau homme', 'coiffure forme du visage', 'tailler sa barbe', 'posture', 'pr\u00e9sence sociale', 'confiance en soi'],
    longTail: ['looksmaxxing naturel ou chirurgie', 'combien de temps pour voir des r\u00e9sultats looksmaxxing', 'looksmaxxing pour qui', 'routine hebdomadaire homme pdf', 'coach relooking en ligne'],
  },
  en: {
    primary: ['looksmaxxing', 'looksmaxxing for men', 'softmaxxing', 'AI looksmaxxing coach', 'glow up men', 'AI face analysis', 'before after simulation'],
    secondary: ['improve appearance', 'men grooming', 'men clothing style', 'men skincare', 'haircut face shape', 'beard trimming', 'posture', 'social presence', 'self-confidence'],
    longTail: ['natural looksmaxxing vs surgery', 'how long does looksmaxxing take', 'who is looksmaxxing for', 'weekly men routine pdf', 'online makeover coach'],
  },
};

export const KEYWORDS = {
  fr: 'looksmaxxing, looksmaxxing homme, relooking homme, coach IA, glow up homme, analyse visage IA, simulation avant apr\u00e8s, grooming, style, confiance',
  en: 'looksmaxxing, looksmaxxing for men, softmaxxing, AI coach, glow up men, AI face analysis, before after simulation, grooming, style, confidence',
};

/**
 * Public, indexable pages. Each page has one dedicated URL per language.
 * `priority` / `changefreq` feed the sitemap.
 */
export const PUBLIC_PAGES = {
  home: {
    priority: '1.0',
    changefreq: 'weekly',
    paths: { fr: '/fr', en: '/en' },
    meta: {
      fr: {
        title: 'PrimeFace \u2013 Coach IA Looksmaxxing & Relooking Strat\u00e9gique',
        description: 'PrimeFace, votre coach IA en optimisation esth\u00e9tique : analyse photo, simulation avant/apr\u00e8s, routine hebdo PDF et suivi de progression. FR et EN.',
        keywords: KEYWORDS.fr,
      },
      en: {
        title: 'PrimeFace \u2013 AI Looksmaxxing Coach & Strategic Makeover',
        description: 'PrimeFace, your AI aesthetic optimization coach: photo analysis, before/after simulation, weekly PDF routine and progress tracking. In English and French.',
        keywords: KEYWORDS.en,
      },
    },
  },
  features: {
    priority: '0.8',
    changefreq: 'monthly',
    paths: { fr: '/fr/fonctionnalites', en: '/en/features' },
    meta: {
      fr: {
        title: 'Fonctionnalit\u00e9s \u2013 Coach IA & Simulation Avant/Apr\u00e8s | PrimeFace',
        description: 'Les fonctionnalit\u00e9s PrimeFace : coach IA expert, analyse photo sur 5 axes, simulation avant/apr\u00e8s, routine hebdomadaire PDF, suivi et rappels par email.',
        keywords: 'fonctionnalit\u00e9s PrimeFace, coach IA looksmaxxing, analyse photo visage, simulation avant apr\u00e8s, routine PDF, suivi progression, rappels email, relooking homme',
      },
      en: {
        title: 'Features \u2013 AI Coach, Before/After Simulation | PrimeFace',
        description: 'Explore PrimeFace features: expert AI coach, 5-axis photo analysis, before/after simulation, weekly PDF routine, progress tracking and email reminders.',
        keywords: 'PrimeFace features, AI looksmaxxing coach, face photo analysis, before after simulation, PDF routine, progress tracking, email reminders, men makeover',
      },
    },
  },
  guide: {
    priority: '0.9',
    changefreq: 'monthly',
    paths: { fr: '/fr/guide-looksmaxxing', en: '/en/looksmaxxing-guide' },
    meta: {
      fr: {
        title: 'Guide Looksmaxxing : la m\u00e9thode des 5 axes | PrimeFace',
        description: 'Guide complet du looksmaxxing naturel : visage, structure corporelle, style, grooming et pr\u00e9sence. M\u00e9thode en 5 axes, erreurs \u00e0 \u00e9viter et plan d\u2019action.',
        keywords: 'guide looksmaxxing, looksmaxxing naturel, looksmaxxing homme, am\u00e9liorer son apparence, grooming homme, style vestimentaire, posture, pr\u00e9sence, m\u00e9thode 5 axes',
      },
      en: {
        title: 'Looksmaxxing Guide: the 5-Axis Method | PrimeFace',
        description: 'Complete natural looksmaxxing guide: face, body structure, style, grooming and presence. The 5-axis method, mistakes to avoid and a realistic action plan.',
        keywords: 'looksmaxxing guide, natural looksmaxxing, looksmaxxing for men, improve appearance, men grooming, clothing style, posture, presence, 5 axis method',
      },
    },
  },
  tips: {
    priority: '0.8',
    changefreq: 'weekly',
    paths: { fr: '/fr/conseils', en: '/en/tips' },
    meta: {
      fr: {
        title: 'Conseils Looksmaxxing : peau, barbe, posture, style | PrimeFace',
        description: 'Articles courts et concrets par axe : peau, barbe, coiffure, sourire, posture, musculation, style, accessoires, finitions, pr\u00e9sence, photo et routine.',
        keywords: 'conseils looksmaxxing, soin peau homme, barbe, coiffure, sourire, posture, musculation, style homme, accessoires, sourcils, photo portrait, routine',
      },
      en: {
        title: 'Looksmaxxing Tips: skin, beard, posture, style | PrimeFace',
        description: 'Short, concrete articles per axis: skin, beard, haircut, smile, posture, strength training, style, accessories, grooming details, presence, photo and routine.',
        keywords: 'looksmaxxing tips, men skincare, beard, haircut, smile, posture, strength training, men style, accessories, eyebrows, portrait photo, routine',
      },
    },
  },
  faq: {
    priority: '0.7',
    changefreq: 'monthly',
    paths: { fr: '/fr/faq', en: '/en/faq' },
    meta: {
      fr: {
        title: 'FAQ \u2013 Questions fr\u00e9quentes sur PrimeFace et le Looksmaxxing',
        description: 'Toutes les r\u00e9ponses sur PrimeFace : looksmaxxing naturel ou chirurgie, d\u00e9lai des r\u00e9sultats, coach IA, photos et confidentialit\u00e9, mode invit\u00e9, simulation.',
        keywords: 'FAQ PrimeFace, questions looksmaxxing, looksmaxxing naturel ou chirurgie, r\u00e9sultats looksmaxxing, coach IA, confidentialit\u00e9 photos, mode invit\u00e9, simulation avant apr\u00e8s',
      },
      en: {
        title: 'FAQ \u2013 Frequently Asked Questions about PrimeFace',
        description: 'Answers about PrimeFace: natural looksmaxxing vs surgery, how long results take, the AI coach, photos and privacy, guest mode and before/after simulation.',
        keywords: 'PrimeFace FAQ, looksmaxxing questions, natural looksmaxxing vs surgery, looksmaxxing results, AI coach, photo privacy, guest mode, before after simulation',
      },
    },
  },
  privacy: {
    priority: '0.3',
    changefreq: 'yearly',
    paths: { fr: '/fr/confidentialite', en: '/en/privacy' },
    meta: {
      fr: {
        title: 'Politique de confidentialit\u00e9 | PrimeFace',
        description: 'Comment PrimeFace (OpenMeta SAS) collecte, utilise et prot\u00e8ge vos donn\u00e9es : photos, profil, conversations, dur\u00e9es de conservation et vos droits RGPD.',
        keywords: 'politique de confidentialit\u00e9, donn\u00e9es personnelles, RGPD, photos, conservation, droits, PrimeFace, OpenMeta',
      },
      en: {
        title: 'Privacy Policy | PrimeFace',
        description: 'How PrimeFace (OpenMeta SAS) collects, uses and protects your data: photos, profile, conversations, retention periods and your GDPR rights explained.',
        keywords: 'privacy policy, personal data, GDPR, photos, retention, rights, PrimeFace, OpenMeta',
      },
    },
  },
  terms: {
    priority: '0.3',
    changefreq: 'yearly',
    paths: { fr: '/fr/conditions', en: '/en/terms' },
    meta: {
      fr: {
        title: 'Conditions g\u00e9n\u00e9rales d\u2019utilisation | PrimeFace',
        description: 'Conditions d\u2019utilisation de PrimeFace : acc\u00e8s au service, mode invit\u00e9, contenus g\u00e9n\u00e9r\u00e9s par IA, photos, propri\u00e9t\u00e9 intellectuelle et responsabilit\u00e9.',
        keywords: 'conditions g\u00e9n\u00e9rales d\u2019utilisation, CGU, PrimeFace, OpenMeta SAS, service IA, responsabilit\u00e9, droit applicable',
      },
      en: {
        title: 'Terms of Service | PrimeFace',
        description: 'PrimeFace terms of service: access to the service, guest mode, AI-generated content, photos, intellectual property, liability and governing law.',
        keywords: 'terms of service, terms of use, PrimeFace, OpenMeta SAS, AI service, liability, governing law',
      },
    },
  },
};

/**
 * Short thematic articles (one per axis lever). URL + meta live here so the sitemap
 * generator and hreflang stay in sync; the body copy lives in articlesContent.js.
 */
export const ARTICLES = {
  skin: {
    axis: 1,
    paths: { fr: '/fr/conseils/soin-de-la-peau-homme', en: '/en/tips/mens-skincare-routine' },
    meta: {
      fr: { title: 'Soin de la peau homme : la routine minimaliste | PrimeFace', description: 'Routine de soin de la peau pour homme en 3 gestes : nettoyant, hydratant, protection solaire. Choisir ses produits, \u00e9viter les erreurs et tenir sur la dur\u00e9e.', keywords: 'soin de la peau homme, routine skincare homme, hydratant, nettoyant visage, protection solaire, peau grasse, peau s\u00e8che, looksmaxxing' },
      en: { title: 'Men\u2019s Skincare: the Minimalist Routine That Sticks | PrimeFace', description: 'A 3-step men\u2019s skincare routine: cleanser, moisturizer, sunscreen. How to choose products, avoid common mistakes and stay consistent over time.', keywords: 'men skincare, skincare routine men, moisturizer, face cleanser, sunscreen, oily skin, dry skin, looksmaxxing' },
    },
  },
  beard: {
    axis: 4,
    paths: { fr: '/fr/conseils/tailler-sa-barbe', en: '/en/tips/beard-trimming-guide' },
    meta: {
      fr: { title: 'Tailler sa barbe : lignes, longueur et entretien | PrimeFace', description: 'Comment tailler sa barbe selon la forme du visage : ligne de joue, ligne de cou, longueur, outils et entretien hebdomadaire pour un rendu net et naturel.', keywords: 'tailler sa barbe, ligne de cou, ligne de joue, forme du visage, tondeuse, huile \u00e0 barbe, entretien barbe, grooming' },
      en: { title: 'Beard Trimming: Lines, Length and Maintenance | PrimeFace', description: 'How to trim your beard for your face shape: cheek line, neckline, length, tools and weekly maintenance for a sharp, natural result that lasts.', keywords: 'beard trimming, beard neckline, cheek line, face shape, beard trimmer, beard oil, beard maintenance, grooming' },
    },
  },
  posture: {
    axis: 2,
    paths: { fr: '/fr/conseils/ameliorer-sa-posture', en: '/en/tips/improve-your-posture' },
    meta: {
      fr: { title: 'Am\u00e9liorer sa posture en 10 minutes par jour | PrimeFace', description: 'Posture : les 3 corrections qui changent l\u2019allure (\u00e9paules, nuque, bassin), une routine de 10 minutes, les erreurs au bureau et comment mesurer vos progr\u00e8s.', keywords: 'am\u00e9liorer sa posture, posture \u00e9paules, dos vo\u00fbt\u00e9, mobilit\u00e9 thoracique, routine posture, allure, structure corporelle, looksmaxxing' },
      en: { title: 'Improve Your Posture in 10 Minutes a Day | PrimeFace', description: 'Posture: the 3 corrections that change your bearing (shoulders, neck, pelvis), a 10-minute routine, common desk mistakes and how to measure your progress.', keywords: 'improve posture, shoulder posture, rounded back, thoracic mobility, posture routine, bearing, body structure, looksmaxxing' },
    },
  },
  style: {
    axis: 3,
    paths: { fr: '/fr/conseils/style-vestimentaire-homme', en: '/en/tips/mens-clothing-style' },
    meta: {
      fr: { title: 'Style homme : coupe, couleurs, coh\u00e9rence | PrimeFace', description: 'Les bases du style masculin : coupe ajust\u00e9e, palette de 3 couleurs neutres plus un accent, registre coh\u00e9rent, retouches et pi\u00e8ces essentielles sans gros budget.', keywords: 'style vestimentaire homme, coupe ajust\u00e9e, palette couleurs homme, garde-robe capsule, retouches, smart casual, essentiels homme, looksmaxxing' },
      en: { title: 'Men\u2019s Clothing Style: Fit, Colors, Coherence | PrimeFace', description: 'The basics of men\u2019s style: proper fit, a palette of 3 neutrals plus one accent, a coherent register, alterations and essential pieces without a big budget.', keywords: 'men clothing style, proper fit, color palette men, capsule wardrobe, alterations, smart casual, men essentials, looksmaxxing' },
    },
  },
  hair: {
    axis: 1,
    paths: { fr: '/fr/conseils/coiffure-forme-du-visage', en: '/en/tips/haircut-face-shape' },
    meta: {
      fr: { title: 'Coiffure et forme du visage : la bonne coupe | PrimeFace', description: 'Quelle coupe pour un visage rond, carr\u00e9, long ou ovale ? Volume, longueur des c\u00f4t\u00e9s, produits coiffants et fr\u00e9quence chez le coiffeur pour un r\u00e9sultat durable.', keywords: 'coiffure forme du visage, coupe homme visage rond, visage carr\u00e9, visage long, volume cheveux, produit coiffant, coiffeur, looksmaxxing' },
      en: { title: 'Haircut and Face Shape: Choosing the Right Cut | PrimeFace', description: 'Which haircut for a round, square, long or oval face? Volume, side length, styling products and barber frequency for a result that lasts between visits.', keywords: 'haircut face shape, round face haircut men, square face, long face, hair volume, styling product, barber, looksmaxxing' },
    },
  },
  presence: {
    axis: 5,
    paths: { fr: '/fr/conseils/presence-sociale', en: '/en/tips/social-presence' },
    meta: {
      fr: { title: 'Pr\u00e9sence sociale : regard, voix et gestuelle | PrimeFace', description: 'Travailler sa pr\u00e9sence : contact visuel, voix pos\u00e9e, gestes ouverts et \u00e9nergie. Des exercices simples pour que votre apparence soit per\u00e7ue \u00e0 sa juste valeur.', keywords: 'pr\u00e9sence sociale, contact visuel, voix pos\u00e9e, langage corporel, gestuelle, confiance en soi, charisme, looksmaxxing' },
      en: { title: 'Social Presence: Gaze, Voice and Body Language | PrimeFace', description: 'Working on your presence: eye contact, a steady voice, open gestures and energy. Simple exercises so your appearance is perceived at its true value.', keywords: 'social presence, eye contact, steady voice, body language, gestures, self-confidence, charisma, looksmaxxing' },
    },
  },
  sleep: {
    axis: 2,
    paths: { fr: '/fr/conseils/sommeil-energie-apparence', en: '/en/tips/sleep-energy-appearance' },
    meta: {
      fr: { title: 'Sommeil et \u00e9nergie : l\u2019impact visible sur le visage | PrimeFace', description: 'Comment le sommeil, la lumi\u00e8re du jour et l\u2019hydratation changent le teint, les cernes et l\u2019\u00e9nergie per\u00e7ue. Protocole simple sur 7 jours et erreurs \u00e0 \u00e9viter.', keywords: 'sommeil apparence, cernes, teint fatigu\u00e9, hygi\u00e8ne de sommeil, lumi\u00e8re du jour, hydratation, \u00e9nergie, looksmaxxing' },
      en: { title: 'Sleep and Energy: the Visible Impact on Your Face | PrimeFace', description: 'How sleep, daylight and hydration change your complexion, dark circles and perceived energy. A simple 7-day protocol and the mistakes to avoid.', keywords: 'sleep appearance, dark circles, tired complexion, sleep hygiene, daylight, hydration, energy, looksmaxxing' },
    },
  },
  routine: {
    axis: 4,
    paths: { fr: '/fr/conseils/routine-constance', en: '/en/tips/routine-consistency' },
    meta: {
      fr: { title: 'Routine et constance : tenir sur la dur\u00e9e | PrimeFace', description: 'Construire une routine d\u2019apparence tenable : ancrage aux habitudes existantes, dur\u00e9e minimale, rappels, suivi mensuel et gestion des rechutes sans culpabilit\u00e9.', keywords: 'routine homme, constance, habitudes, rappels, suivi progression, routine hebdomadaire, discipline, looksmaxxing' },
      en: { title: 'Routine and Consistency: Making It Last | PrimeFace', description: 'Building a sustainable appearance routine: anchoring to existing habits, minimum duration, reminders, monthly review and handling relapses without guilt.', keywords: 'men routine, consistency, habits, reminders, progress tracking, weekly routine, discipline, looksmaxxing' },
    },
  },
  smile: {
    axis: 1,
    paths: { fr: '/fr/conseils/sourire-dents-hygiene', en: '/en/tips/smile-teeth-care' },
    meta: {
      fr: { title: 'Sourire et dents : hygi\u00e8ne, blancheur, confiance | PrimeFace', description: 'Un sourire net change la lecture du visage : brossage efficace, fil dentaire, d\u00e9tartrage, blanchiment raisonnable et exercices pour sourire naturellement.', keywords: 'sourire homme, dents blanches, hygi\u00e8ne dentaire, blanchiment dentaire, d\u00e9tartrage, fil dentaire, sourire naturel, looksmaxxing' },
      en: { title: 'Smile and Teeth: Hygiene, Whitening, Confidence | PrimeFace', description: 'A clean smile changes how a face is read: effective brushing, flossing, professional cleaning, reasonable whitening and simple exercises to smile naturally.', keywords: 'men smile, white teeth, dental hygiene, teeth whitening, dental cleaning, flossing, natural smile, looksmaxxing' },
    },
  },
  training: {
    axis: 2,
    paths: { fr: '/fr/conseils/musculation-silhouette-homme', en: '/en/tips/strength-training-physique' },
    meta: {
      fr: { title: 'Musculation et silhouette : carrure et taille | PrimeFace', description: 'Construire une silhouette en V sans y passer sa vie : trois s\u00e9ances de force par semaine, prot\u00e9ines, sommeil, marche et des indicateurs mesurables chaque mois.', keywords: 'musculation homme, silhouette en V, carrure, composition corporelle, prot\u00e9ines, s\u00e9ance de force, tour de taille, looksmaxxing' },
      en: { title: 'Strength Training for Your Physique: Frame and Waist | PrimeFace', description: 'Build a V-shaped physique without living in the gym: three strength sessions a week, protein, sleep, walking and measurable indicators month after month.', keywords: 'men strength training, V shape physique, frame, body composition, protein, strength session, waist size, looksmaxxing' },
    },
  },
  accessories: {
    axis: 3,
    paths: { fr: '/fr/conseils/lunettes-montre-accessoires-homme', en: '/en/tips/mens-accessories-glasses-watch' },
    meta: {
      fr: { title: 'Accessoires homme : lunettes, montre, chaussures | PrimeFace', description: 'Choisir ses lunettes selon la forme du visage, une montre \u00e0 l\u2019\u00e9chelle du poignet, des chaussures entretenues et une r\u00e8gle : un seul accessoire fort \u00e0 la fois.', keywords: 'accessoires homme, lunettes forme du visage, montre homme, chaussures homme, ceinture, lunettes de soleil, d\u00e9tails style, looksmaxxing' },
      en: { title: 'Men\u2019s Accessories: Glasses, Watch, Shoes | PrimeFace', description: 'Choosing glasses for your face shape, a watch scaled to your wrist, well-kept shoes and one simple rule: a single statement accessory at a time, never more.', keywords: 'men accessories, glasses face shape, men watch, men shoes, belt, sunglasses, style details, looksmaxxing' },
    },
  },
  eyebrows: {
    axis: 4,
    paths: { fr: '/fr/conseils/sourcils-finitions-grooming', en: '/en/tips/eyebrows-grooming-details' },
    meta: {
      fr: { title: 'Sourcils et finitions : les d\u00e9tails du grooming | PrimeFace', description: 'Les d\u00e9tails qui font la diff\u00e9rence : sourcils nettoy\u00e9s sans les affiner, poils du nez et des oreilles, ongles courts, mains hydrat\u00e9es et un parfum discret.', keywords: 'sourcils homme, \u00e9piler sourcils homme, poils nez oreilles, ongles homme, parfum homme, finitions grooming, d\u00e9tails apparence, looksmaxxing' },
      en: { title: 'Eyebrows and Finishing Touches: Grooming Details | PrimeFace', description: 'The details that make the difference: tidy eyebrows without thinning them, nose and ear hair, short nails, moisturized hands and a discreet fragrance.', keywords: 'men eyebrows, eyebrow grooming men, nose ear hair, men nails, men fragrance, grooming details, appearance details, looksmaxxing' },
    },
  },
  photo: {
    axis: 5,
    paths: { fr: '/fr/conseils/reussir-sa-photo-portrait', en: '/en/tips/better-profile-photo' },
    meta: {
      fr: { title: 'R\u00e9ussir sa photo : lumi\u00e8re, angle, expression | PrimeFace', description: 'Lumi\u00e8re naturelle de face, objectif \u00e0 hauteur des yeux, menton l\u00e9g\u00e8rement avanc\u00e9, expression d\u00e9tendue : la m\u00e9thode pour une photo de profil fid\u00e8le et flatteuse.', keywords: 'r\u00e9ussir sa photo, photo de profil homme, lumi\u00e8re portrait, angle photo, expression naturelle, photog\u00e9nique, photo LinkedIn, looksmaxxing' },
      en: { title: 'A Better Photo: Light, Angle, Expression | PrimeFace', description: 'Natural front light, lens at eye level, chin slightly forward, relaxed expression: the method for a profile photo that is both faithful and flattering to you.', keywords: 'better profile photo, men profile picture, portrait light, photo angle, natural expression, photogenic, LinkedIn photo, looksmaxxing' },
    },
  },
};

export const ARTICLE_KEYS = Object.keys(ARTICLES);
export const articlePageKey = (key) => `article_${key}`;
export const articleKeyFromPage = (pageKey) => (pageKey.startsWith('article_') ? pageKey.slice('article_'.length) : null);

// Register every article as a public page (sitemap, hreflang, routing).
ARTICLE_KEYS.forEach((key) => {
  PUBLIC_PAGES[articlePageKey(key)] = { priority: '0.6', changefreq: 'monthly', paths: ARTICLES[key].paths, meta: ARTICLES[key].meta };
});

/** Private application areas: blocked in robots.txt and tagged noindex. */
export const PRIVATE_PATH_PREFIXES = [
  '/dashboard', '/coach', '/progress', '/routine', '/history', '/settings', '/simulation', '/admin', '/onboarding',
];

export const absoluteUrl = (path = '/') => `${SITE_URL}${path}`;
export const pagePath = (key, lang) => PUBLIC_PAGES[key].paths[lang] || PUBLIC_PAGES[key].paths[DEFAULT_LANG];
export const pageMeta = (key, lang) => PUBLIC_PAGES[key].meta[lang] || PUBLIC_PAGES[key].meta[DEFAULT_LANG];

/** hreflang map for a page: { fr, en, 'x-default' } (relative paths). */
export const alternatesFor = (key) => ({
  fr: pagePath(key, 'fr'),
  en: pagePath(key, 'en'),
  'x-default': pagePath(key, DEFAULT_LANG),
});

/** Resolve a pathname to { key, lang } or null. */
export const findPublicPage = (pathname) => {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  for (const key of Object.keys(PUBLIC_PAGES)) {
    for (const lang of LANGS) {
      if (PUBLIC_PAGES[key].paths[lang] === clean) return { key, lang };
    }
  }
  return null;
};

export const isPrivatePath = (pathname) => PRIVATE_PATH_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));

/** Flat list of every public URL, used by the sitemap generator. */
export const listPublicUrls = () => Object.keys(PUBLIC_PAGES).flatMap(key => LANGS.map(lang => ({
  key,
  lang,
  path: pagePath(key, lang),
  loc: absoluteUrl(pagePath(key, lang)),
  priority: PUBLIC_PAGES[key].priority,
  changefreq: PUBLIC_PAGES[key].changefreq,
  alternates: alternatesFor(key),
})));
